import { useQuery } from '@tanstack/react-query';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Plus, CircleAlert, ChevronRight, HelpCircle } from 'lucide-react';
import { useAccessToken } from '@/auth/useAccessToken';
import { openOnboarding } from '@/components/OnboardingDialog';
import { ticketsApi } from '@/features/tickets/ticketsApi';
import { StatusBadge } from '@/features/tickets/StatusBadge';
import type { MyTicketsListItem } from '@/features/tickets/schema';

/**
 * /tickets — the signed-in user's own tickets, newest-first.
 *
 * Loads via TanStack Query so the cache survives navigation back from
 * /new-ticket — submitting a ticket invalidates this query (see
 * CreateTicketForm.onSuccess) so the new ticket appears immediately on
 * return without a server round-trip the second time the page is opened.
 */
export function MyTickets() {
  const getAccessToken = useAccessToken();
  const location = useLocation() as { state?: { justCreated?: number } };

  const tickets = useQuery({
    queryKey: ['tickets', 'mine'],
    queryFn: ({ signal }) => ticketsApi.listMine(getAccessToken, signal)
  });

  return (
    <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
      <header className="flex flex-wrap items-start justify-between gap-3 mb-6">
        <div className="min-w-0">
          <h1 className="text-xl sm:text-2xl font-semibold">My Tickets</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Everything you've submitted, newest first.{' '}
            <button
              type="button"
              onClick={openOnboarding}
              className="inline-flex items-center gap-1 text-primary hover:underline align-baseline"
            >
              <HelpCircle className="w-3.5 h-3.5" /> How it works
            </button>
          </p>
        </div>
        <Link
          to="/new-ticket"
          className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-md bg-primary text-primary-foreground px-3.5 py-2 text-sm font-medium hover:opacity-90"
        >
          <Plus className="w-4 h-4" /> New Ticket
        </Link>
      </header>

      {location.state?.justCreated !== undefined && (
        <div className="mb-4 rounded-md border border-primary/30 bg-primary/10 px-4 py-2.5 text-sm">
          Ticket <span className="font-mono">#{location.state.justCreated}</span> submitted. Someone in IT will be in touch shortly.
        </div>
      )}

      {tickets.isPending && <ListSkeleton />}

      {tickets.isError && (
        <div className="rounded-md border border-destructive/40 bg-destructive/10 px-4 py-3 text-sm text-destructive flex items-start gap-2">
          <CircleAlert className="w-4 h-4 mt-0.5" />
          <span>Couldn't load your tickets: {(tickets.error as Error).message}</span>
        </div>
      )}

      {tickets.data && tickets.data.length === 0 && <EmptyState />}

      {tickets.data && tickets.data.length > 0 && <TicketsTable tickets={tickets.data} />}
    </div>
  );
}

function TicketsTable({ tickets }: { tickets: MyTicketsListItem[] }) {
  const navigate = useNavigate();
  return (
    <>
      {/* Phones: stacked cards (the table is too wide to read on a 375px screen). */}
      <ul className="md:hidden space-y-2">
        {tickets.map((t) => (
          <li key={t.id}>
            <Link
              to={`/tickets/${t.ticketNumber}`}
              className="block rounded-xl border bg-card p-4 hover:bg-muted/30 transition-colors active:scale-[0.998]"
            >
              <div className="flex items-center justify-between gap-2">
                <span className="font-mono text-xs text-muted-foreground">#{t.ticketNumber}</span>
                <StatusBadge status={t.status} />
              </div>
              <div className="mt-1 font-medium leading-snug">{t.subject}</div>
              <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
                <span className="inline-block px-2 py-0.5 rounded-full bg-muted">{t.category}</span>
                <span>· {formatDistanceToNow(new Date(t.updatedUtc), { addSuffix: true })}</span>
                <span>
                  · {t.assigneeDisplayName ?? <span className="italic">Unassigned</span>}
                </span>
              </div>
            </Link>
          </li>
        ))}
      </ul>

      {/* md+: the full table. */}
      <div className="hidden md:block rounded-xl border bg-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="text-left text-xs uppercase tracking-wider text-muted-foreground bg-muted/30">
            <tr>
              <th className="px-5 py-3 font-medium">#</th>
              <th className="px-3 py-3 font-medium">Subject</th>
              <th className="px-3 py-3 font-medium">Category</th>
              <th className="px-3 py-3 font-medium">Status</th>
              <th className="px-3 py-3 font-medium">Last update</th>
              <th className="px-3 py-3 font-medium">Assigned</th>
              <th className="px-3 py-3 w-8" aria-hidden="true" />
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {tickets.map((t) => {
              const open = () => navigate(`/tickets/${t.ticketNumber}`);
              return (
                // The whole row is the click target (cursor-pointer + hover +
                // a trailing chevron signal it). role/tabIndex/Enter keep it
                // keyboard-accessible like the link it replaces.
                <tr
                  key={t.id}
                  onClick={open}
                  onKeyDown={(e) => { if (e.key === 'Enter') open(); }}
                  role="link"
                  tabIndex={0}
                  aria-label={`Open ticket #${t.ticketNumber}: ${t.subject}`}
                  className="group cursor-pointer hover:bg-muted/40 transition-colors outline-none focus-visible:ring-1 focus-visible:ring-primary/40"
                >
                  <td className="px-5 py-3 font-mono text-muted-foreground">#{t.ticketNumber}</td>
                  <td className="px-3 py-3 font-medium text-primary group-hover:underline">{t.subject}</td>
                  <td className="px-3 py-3">
                    <span className="inline-block px-2 py-0.5 rounded-full bg-muted text-xs">{t.category}</span>
                  </td>
                  <td className="px-3 py-3">
                    <StatusBadge status={t.status} />
                  </td>
                  <td className="px-3 py-3 text-muted-foreground">{formatDistanceToNow(new Date(t.updatedUtc), { addSuffix: true })}</td>
                  <td className="px-3 py-3">{t.assigneeDisplayName ?? <span className="text-muted-foreground italic">Unassigned</span>}</td>
                  <td className="px-3 py-3 text-muted-foreground">
                    <ChevronRight className="w-4 h-4 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function EmptyState() {
  return (
    <div className="rounded-xl border border-dashed bg-card p-10 text-center">
      <div className="text-base font-medium">You haven't submitted any tickets yet.</div>
      <p className="text-sm text-muted-foreground mt-1.5">
        File one when you need IT help — it's the fastest way to get a response.
      </p>
      <Link
        to="/new-ticket"
        className="mt-5 inline-flex items-center gap-1.5 rounded-md bg-primary text-primary-foreground px-3.5 py-2 text-sm font-medium hover:opacity-90"
      >
        <Plus className="w-4 h-4" /> Submit your first ticket
      </Link>
    </div>
  );
}

function ListSkeleton() {
  return (
    <div className="rounded-xl border bg-card overflow-hidden">
      <div className="px-5 py-3 bg-muted/30">
        <div className="h-3 w-24 bg-muted rounded animate-pulse" />
      </div>
      {Array.from({ length: 4 }).map((_, i) => (
        <div key={i} className="px-5 py-4 border-t flex items-center gap-3">
          <div className="h-3 w-12 bg-muted rounded animate-pulse" />
          <div className="h-3 flex-1 bg-muted rounded animate-pulse" />
          <div className="h-3 w-20 bg-muted rounded animate-pulse" />
        </div>
      ))}
    </div>
  );
}
